import { AdminPreferenceItem } from './../models/domain/admin.preference.item';
import { GetAdminPreferenceByNameRequest } from './../models/ss-api/requests/get.admin.preference.by.name.request';
import { Injectable } from '@angular/core';
import { ssApiService } from './ss-api.service';
import { Observable } from 'rxjs/Observable';
import { GlobalService } from './global.service';
import { AdvancedSearchItem } from '../../app/models/domain/advanced.search.item';
import { AdvancedSearchResultMemberItemArray, GlobalSearchResults } from '../../app/models/domain/advanced.searche.results';
import { AdminSearchItemArray } from '../../app/models/domain/admin.search.item';
import { IBaseResponse } from '../../app/models/comm-http/responses/BaseResponse';
import { NewSearchPresetRequest } from '../../app/models/ss-api/requests/new.search.preset.request';
import { SaveAdminPreferenceRequest } from '../models/ss-api/requests/save.admin.preference.request';

interface CheckBoxOption {
    Label: string;
    LabelHTML: string;
    Value: number;
}

@Injectable()
export class SearchService {
    static readonly Settings = {
        HistoryCount: 5,
        ResultsCount: 100,
        ResultsLifetime: 24
    };

    static readonly DefaultMemberTypeOptions: Array<CheckBoxOption> = [
        { Label: 'Active Member', LabelHTML: 'Active Member', Value: 100 },
        { Label: 'Cancelled Member', LabelHTML: 'Cancelled Member', Value: 110 },
        { Label: 'Active Designer', LabelHTML: 'Active Designer', Value: 200 },
        { Label: 'Cancelled Designer', LabelHTML: 'Cancelled Designer', Value: 210 },
        { Label: 'Orphaned Designer', LabelHTML: 'Orphaned Designer', Value: 220 }
    ];

    DefaultSearchSettings: AdvancedSearchItem = new AdvancedSearchItem();
    SearchHistory: AdminSearchItemArray = new AdminSearchItemArray();
    SearchSettings: AdvancedSearchItem;
    SearchResults: GlobalSearchResults<AdvancedSearchResultMemberItemArray>;
    ProductOverviewResults: GlobalSearchResults<AdvancedSearchResultMemberItemArray>;
    SearchPresets: AdminSearchItemArray = new AdminSearchItemArray();
    SalesReps: Array<CheckBoxOption>;
    Products: Array<CheckBoxOption>;
    AddOnProducts: Array<CheckBoxOption>;
    MemberTypes: Array<CheckBoxOption>;

    constructor(private globalService: GlobalService, private api: ssApiService) {

        this.InitializeGlobalObjects();
        this.SetupDefaultSettings();

    }

    private async InitializeGlobalObjects() {

        this.SalesReps = [];
        this.Products = [];
        this.AddOnProducts = [];
        this.MemberTypes = SearchService.DefaultMemberTypeOptions;

        await this.globalService.GetSalesRepresentatives().then(response => {

            response.forEach(item => {
                let option = {
                    Label: item.FirstName + ' ' + item.LastName,
                    LabelHTML: item.FirstName,
                    Value: item.ID
                };

                this.SalesReps.push(option);
            })

        })

        console.log("InitializeGlobalObjects");

        await this.globalService.GetStructureStudiosProductsBasics().then(response => {

            //Process the GIS Add Ons first, for positioning
            response.forEach(item => {
                if (item.ModuleType == 'GIS Subscription') {
                    let option = {
                        Label: item.Name,
                        LabelHTML: item.Name.toLowerCase().replace(/\s+/g, ''),
                        Value: item.ID
                    };

                    this.AddOnProducts.push(option);    
                }
              
            })

            //unintuitively sort this desc, because 4 comes before 10, and we don't have an int qty
            this.AddOnProducts.sort((a, b) =>
                b.Label.localeCompare(a.Label)
            );   

            //Standard products and YARD add ons
            response.forEach(item => {
                if (item.ModuleType == 'VizTerra' || item.ModuleType == 'PoolStudio' || item.Name == 'VIP3D') {
                    let option = {
                        Label: item.Name,
                        LabelHTML: item.Name.toLowerCase().replace(/\s+/g, ''),
                        Value: item.ID
                    };

                    this.Products.push(option);    
                }

                if (item.ModuleType == 'Yard') {
                    let option = {
                        Label: item.Name,
                        LabelHTML: item.Name.toLowerCase().replace(/\s+/g, ''),
                        Value: item.ID
                    };

                    this.AddOnProducts.push(option);    
                }

            })

        })
    }

    public GetSearchSettings() {
        let request: GetAdminPreferenceByNameRequest = {
            PreferenceName: 'SS_Search_Display_Settings',
            adminID: this.globalService.AdminId
        }
        return this.api.GetAdminPreferenceByName(request).toPromise()
    }

    public SaveDisplaySettings(searchSettings: AdvancedSearchItem) {
        let item: AdminPreferenceItem = {
            AdminID: this.globalService.AdminId,
            PreferenceName: 'SS_Search_Display_Settings',
            PreferenceValue: JSON.stringify(searchSettings),
            PreferenceType: 1
        }
        let request: SaveAdminPreferenceRequest = {
            adminPreferenceItem: item
        }

        let disposable = this.api.SaveAdminPreference(request).subscribe(response => {
            disposable.unsubscribe();
        })
    }

    public LoadHistory(): Observable<IBaseResponse<AdminSearchItemArray>> {
        return this.api.GetAdminSearchHistory().map(response => {
            this.SearchHistory = response.Data;
            return response
        })
    }

    public LoadPresets(): Observable<IBaseResponse<AdminSearchItemArray>> {
        return this.api.GetAdminSearchPresets().map(response => {
            this.SearchPresets = response.Data;
            return response;
        })
    }

    // private InitializeSearchStore() {

    //     // Load from localStorage
    //     let localHistory = JSON.parse(localStorage.getItem('SS_SearchHistory'));
    //     let localSettings = JSON.parse(localStorage.getItem('SS_SearchSettings'));
    //     let localPresets = JSON.parse(localStorage.getItem('SS_SearchPresets'));

    //     // Set values from local storage or create new instances if they don't exist
    //     this.SearchHistory = localHistory ? localHistory : new Array<StoredSearchItem>();
    //     this.SearchSettings = localSettings ? localSettings : this.DefaultSearchSettings;
    //     this.SearchPresets = localPresets ? localPresets : new Array<StoredSearchItem>();

    // }

    private SetupDefaultSettings() {
        this.DefaultSearchSettings.showMemberID = true;
        this.DefaultSearchSettings.showFirstName = true;
        this.DefaultSearchSettings.showDesignerName = true;
        this.DefaultSearchSettings.showLastName = true;
        this.DefaultSearchSettings.showCompanyName = true;
        this.DefaultSearchSettings.showEmail = true;
        this.DefaultSearchSettings.showPhone1 = true;
        this.DefaultSearchSettings.showCDKey = true;
        this.DefaultSearchSettings.showStatus = true;
        this.DefaultSearchSettings.showProductID = true;
        this.DefaultSearchSettings.showMemberType = true;
        this.DefaultSearchSettings.showExpirationDate = false;
        this.DefaultSearchSettings.showState = true;
        this.DefaultSearchSettings.showVersion = true;
    }

    public UpdateSearchSettings(searchSettings: AdvancedSearchItem) {
        this.SearchSettings = searchSettings;
        localStorage.setItem('SS_SearchSettings', JSON.stringify(this.SearchSettings));
    }

    public AddSearchPreset(label: string, searchObject: AdvancedSearchItem) {
        let request: NewSearchPresetRequest = {
            Label: label,
            SearchRequest: searchObject
        }

        return this.api.NewSearchPreset(request).map(response => {
            return response.Data;
        })
    }

    public DeleteSearchPreset(id: number) {
        return this.api.DeleteAdminSearch(id).map(response => {
            return response.Data;
        })
    }

    public RepeatSearch(id: number): Observable<IBaseResponse<GlobalSearchResults<AdvancedSearchResultMemberItemArray>>> {
        return this.api.RepeatAdminSearch(id).map(response => {
            this.SearchResults = response.Data;
            return response;
        })
    }

    public StartSearch(searchItem: AdvancedSearchItem): Observable<IBaseResponse<GlobalSearchResults<AdvancedSearchResultMemberItemArray>>> {
        return this.api.SearchAdvanced(searchItem).map(response => {
            this.SearchResults = response.Data;
            return response;
        })
    }

    public GetLatestSearch() {
        if (this.SearchHistory.length > 0) {
            return JSON.parse(JSON.stringify(this.SearchHistory[this.SearchHistory.length - 1]));
        } else {
            return this.SearchHistory;
        }
    }

    public SetProductOverviewResults(results) {
        this.ProductOverviewResults = results;
    }

}
